import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useUpdateCategoryGroupMutation,
  useUpdateCategoryMutation,
} from '#budget';
import { ehPortugues, planoDeTraducao } from '#finapp-locale/categoriasPadrao';
import { useCategories } from '#hooks/useCategories';

/**
 * Traduz os nomes de categoria e de grupo que o app cria sozinho quando um
 * arquivo novo nasce. Ver `finapp-locale/categoriasPadrao.ts` — é lá que
 * estão o porquê e as quatro travas.
 *
 * ⚠️ Usa as MUTATIONS do app, não `send()` cru: o grupo carrega no cliente um
 * campo `categories` que não existe na tabela, e mandá-lo faz o servidor
 * recusar com "Field categories does not exist" (medido). A mutation já tira
 * esse campo, checa nome repetido e invalida o cache.
 */
export function useTraduzirCategoriasPadrao() {
  const { i18n } = useTranslation();
  const { data } = useCategories();
  const atualizarGrupo = useUpdateCategoryGroupMutation();
  const atualizarCategoria = useUpdateCategoryMutation();
  const rodou = useRef(false);

  useEffect(() => {
    if (rodou.current || !data || !ehPortugues(i18n.language)) {
      return;
    }

    const grupos = planoDeTraducao(data.grouped ?? []);
    const categorias = planoDeTraducao(data.list ?? []);
    if (grupos.length === 0 && categorias.length === 0) {
      return;
    }

    // marca ANTES de enviar: o efeito reroda quando a lista muda, e a lista
    // muda por causa das próprias renomeações.
    rodou.current = true;

    void (async () => {
      for (const g of grupos) {
        const grupo = (data.grouped ?? []).find(x => x.id === g.id);
        if (!grupo) continue;
        try {
          await atualizarGrupo.mutateAsync({
            group: { ...grupo, name: g.para },
          });
        } catch (erro) {
          console.error('Não consegui traduzir o grupo', g.de, erro);
        }
      }
      for (const c of categorias) {
        const categoria = (data.list ?? []).find(x => x.id === c.id);
        if (!categoria) continue;
        try {
          await atualizarCategoria.mutateAsync({
            category: { ...categoria, name: c.para },
          });
        } catch (erro) {
          console.error('Não consegui traduzir a categoria', c.de, erro);
        }
      }
    })();
    // as mutations são estáveis entre renders; incluí-las relançaria o efeito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, i18n.language]);
}
